import React, { useEffect, useRef, useState } from "react";

/**
 * Punch selfie capture (attendance-photo-punch spec, AC2/AC4/AC9).
 * Opens the front camera via getUserMedia; falls back to a native file input
 * with capture="user" when the camera is unavailable or permission is denied.
 * Output is a JPEG data URL compressed to ≤500 KB before upload.
 */

const MAX_BYTES = 500 * 1024;
const MAX_WIDTH = 720;

function canvasToDataUrl(canvas: HTMLCanvasElement): string {
  // Walk quality down until the payload fits — selfies compress well
  for (const q of [0.8, 0.6, 0.45, 0.3]) {
    const url = canvas.toDataURL("image/jpeg", q);
    // base64 inflates ~4/3; compare decoded size
    if ((url.length - "data:image/jpeg;base64,".length) * 0.75 <= MAX_BYTES) return url;
  }
  return canvas.toDataURL("image/jpeg", 0.25);
}

function drawScaled(source: HTMLVideoElement | HTMLImageElement, w: number, h: number): HTMLCanvasElement {
  const scale = Math.min(1, MAX_WIDTH / w);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  canvas.getContext("2d")!.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export default function CameraCaptureModal({ phase, onCapture, onCancel }: {
  phase: "in" | "out";
  onCapture: (dataUrl: string) => void;
  onCancel: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("Camera not supported on this device/browser.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch {
        setCameraError("Camera access was denied or unavailable. You can take a photo with your device camera instead.");
      }
    }
    start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  function stopStream() {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  function capture() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = drawScaled(video, video.videoWidth, video.videoHeight);
    setPreview(canvasToDataUrl(canvas));
  }

  function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = drawScaled(img, img.naturalWidth, img.naturalHeight);
      URL.revokeObjectURL(url);
      setPreview(canvasToDataUrl(canvas));
    };
    img.onerror = () => { URL.revokeObjectURL(url); setCameraError("Could not read that image — please try again."); };
    img.src = url;
  }

  function confirm() {
    if (!preview) return;
    stopStream();
    onCapture(preview);
  }

  const btn: React.CSSProperties = { padding: "10px 20px", borderRadius: 8, border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.65)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
         onClick={() => { stopStream(); onCancel(); }}>
      <div style={{ background: "white", borderRadius: 14, padding: 20, width: "100%", maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 4px", fontSize: 16, color: "#1e3a8a" }}>
          📸 Photo required to check {phase}
        </h3>
        <p style={{ margin: "0 0 14px", fontSize: 13, color: "#64748b" }}>
          Take a quick selfie to confirm it's you punching {phase === "in" ? "in" : "out"}.
        </p>

        {preview ? (
          <>
            <img src={preview} alt="Punch preview" style={{ width: "100%", borderRadius: 10, marginBottom: 14 }} />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={confirm} style={{ ...btn, flex: 1, background: "#16a34a", color: "white" }}>
                Use this photo
              </button>
              <button onClick={() => setPreview(null)} style={{ ...btn, background: "white", color: "#475569", border: "1px solid #e2e8f0" }}>
                Retake
              </button>
            </div>
          </>
        ) : cameraError ? (
          <>
            <div style={{ background: "#fef9c3", border: "1px solid #fde047", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#854d0e", marginBottom: 14 }}>
              ⚠️ {cameraError}
            </div>
            <label style={{ ...btn, display: "block", textAlign: "center", background: "#1e3a8a", color: "white", marginBottom: 10 }}>
              Take photo with device camera
              <input type="file" accept="image/*" capture="user" onChange={onFilePicked} style={{ display: "none" }} />
            </label>
          </>
        ) : (
          <>
            <video ref={videoRef} playsInline muted style={{ width: "100%", borderRadius: 10, background: "#0f172a", marginBottom: 14, transform: "scaleX(-1)" }} />
            <button onClick={capture} disabled={!ready} style={{ ...btn, width: "100%", background: ready ? "#1e3a8a" : "#94a3b8", color: "white", marginBottom: 10 }}>
              {ready ? "Capture" : "Starting camera…"}
            </button>
          </>
        )}

        <button onClick={() => { stopStream(); onCancel(); }} style={{ ...btn, width: "100%", background: "white", color: "#64748b", border: "1px solid #e2e8f0", marginTop: preview ? 10 : 0 }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
